// src/components/dashboard/OrdersSection.tsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { apiService } from '../../services/api'
import { formatCurrency } from '../../utils/format'  // ← EKLE
import toast from 'react-hot-toast'
import { Package, AlertCircle, CheckCircle, Clock, XCircle, Truck, RefreshCw, Search, Eye, DollarSign, Undo2, ExternalLink } from 'lucide-react'
import OrderDetail from './OrderDetail'
import { useNavigate } from 'react-router-dom'

// API'den gelecek tipler
interface ScrapeOrderItem {
  title: string;
  asin: string | null;
  link: string | null;
  image: string | null;
  price: number | null;
  quantity: number | null;
  category: string;
  /** Amazon'un ödediği TAM oran (%) — sunucudan gelir */
  commissionRate?: number;
  /** Kullanıcı payı oranı (%) — sunucuda hesaplanır */
  userRate?: number;
  /** Kullanıcı payı tutarı — sunucuda hesaplanır */
  userEarnings?: number;
}

interface ScrapeOrder {
  id: string;
  orderId: string;
  orderDateUtc: string | null;
  total: number | null;
  status: string | null;
  itemCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  items?: ScrapeOrderItem[];
}

interface OrdersListResponse {
  page: number;
  pageSize: number;
  total: number;
  results: ScrapeOrder[];
}

interface OrderFilters {
  status: string;
  search: string;
  dateRange: string;
  pageSize: number;
  /** Alıcı hesabı: '' = tümü, '__none__' = henüz okunmamış, aksi halde e-posta */
  buyerEmail: string;
  /** Doğrulama: '' = tümü, 'verified' = doğrulandı, 'unverified' = doğrulanmadı (sunucu kuralı) */
  verification: string;
}

/** /orders/buyer-accounts satırı — filtre listesi bundan dolar */
interface BuyerAccountRow {
  buyerEmail: string | null;
  total: number;
}

// Kategori komisyon oranları tablosu


// Komisyon SUNUCUDA hesaplanır — bu ekranlar yalnızca gösterir.
// Oran tablosu ve kullanıcı payı kuralı tek yerde yaşar:
//   AmazonBonus.Api/Services/CommissionCalculator.cs
// API her kalem için hazır `userRate` (%) ve `userEarnings` (tutar) gönderir;
// eski sürümlerden gelen yanıtlar için `commissionRate / 2` yedeği bırakıldı.
const getCommissionRate = (item: ScrapeOrderItem): number => {
  if (item.userRate !== undefined && item.userRate !== null) return item.userRate;
  if (item.commissionRate !== undefined && item.commissionRate !== null) return item.commissionRate / 2;
  return 0;
};

// Sipariş için komisyon tutarı (sunucudan gelen kalem tutarlarının toplamı)
const calculateOrderCommission = (order: ScrapeOrder): number => {
  if (!order.items || order.items.length === 0) return 0;

  return order.items.reduce((sum, item) => {
    if (item.userEarnings !== undefined && item.userEarnings !== null) return sum + item.userEarnings;
    return sum + ((item.price || 0) * getCommissionRate(item)) / 100;
  }, 0);
};

// Durum çevirisi fonksiyonu
const translateStatus = (status: string | null): string => {
  if (!status) return 'Beklemede'
  
  const statusLower = status.toLowerCase()
  
  // Arriving kontrolü
  if (statusLower.includes('arriving')) return 'Kargoda'

  // DELIVERED KONTROLÜ - CONTAINS KULLAN
  if (statusLower.includes('delivered')) return 'Teslim Edildi'
  
  // Tam eşleşmeler
  switch (statusLower) {
    case 'cancelled':
      return 'İptal Edildi'
    case 'refunded':
      return 'Para İadesi Yapıldı'
    case 'return started':
      return 'İade Talebi Mevcut'
    case 'test':
      return 'Test'
    case 'processing':
      return 'İşleniyor'
    case 'pending':
      return 'Beklemede'
    default:
      return status
  }
}

// Duruma göre komisyon gösterimi
const shouldShowCommissionStrikethrough = (status: string | null): boolean => {
  if (!status) return false
  const statusLower = status.toLowerCase()
  return statusLower === 'cancelled' || statusLower === 'refunded'
}

const shouldShowCommissionPending = (status: string | null): boolean => {
  if (!status) return false
  const statusLower = status.toLowerCase()
  return statusLower === 'return started'
}

// Amazon sipariş URL'i oluştur
const getAmazonOrderUrl = (orderId: string): string => {
  return `https://www.amazon.com/gp/your-account/order-details/?orderID=${orderId}`
}

// Sipariş numarasını tıklanabilir link olarak render et
const OrderIdLink = ({ orderId }: { orderId: string }) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Parent'ın onClick'ini tetiklememek için
    window.open(getAmazonOrderUrl(orderId), '_blank', 'noopener,noreferrer')
  }

  return (
    <button
      onClick={handleClick}
      className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors"
      title="Amazon'da aç"
    >
      {orderId}
      <ExternalLink className="w-3 h-3" />
    </button>
  )
}

export default function OrdersSection({ routeOrderId = null }: { routeOrderId?: string | null }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [orders, setOrders] = useState<ScrapeOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<ScrapeOrder | null>(null)
  
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  })

    const [orderStats, setOrderStats] = useState({
    total: 0,
    delivered: 0,
    pending: 0,
    inTransit: 0,
    cancelled: 0
  })
  
  const [filters, setFilters] = useState<OrderFilters>({
    status: 'Tümü',
    search: '',
    dateRange: 'Son 30 gün',
    pageSize: 10,
    buyerEmail: '',
    verification: ''
  })

  // Alıcı hesabı seçenekleri — kullanıcının siparişlerinde görülen hesaplar.
  // Yeni eklentiyle veri geldikçe dolar; hiç hesap yoksa filtre gösterilmez.
  const [buyerAccounts, setBuyerAccounts] = useState<BuyerAccountRow[]>([])
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/orders/buyer-accounts`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
        })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled && Array.isArray(data?.accounts)) setBuyerAccounts(data.accounts)
      } catch { /* filtre isteğe bağlı; sessizce geç */ }
    })()
    return () => { cancelled = true }
  }, [])

  const orderStatuses = ['Tümü', 'Beklemede', 'Kargoda', 'Teslim Edildi', 'İptal Edildi', 'Para İadesi Yapıldı', 'İade Talebi Mevcut']
  const dateRanges = ['Son 7 gün', 'Son 30 gün', 'Son 3 ay', 'Tüm zamanlar']
  const pageSizes = [10, 25, 50, 100]

  // API'den siparişleri çek
const fetchOrders = async (
  page: number = 1, 
  pageSize: number = filters.pageSize, 
  showLoading: boolean = true,
  statusOverride?: string,
  orderIdFilter?: string,
  buyerEmailOverride?: string,
  verificationOverride?: string
) => {
    if (!user?.id) return

    if (showLoading) setLoading(true)
    else setRefreshing(true)

    try {
      const userId = localStorage.getItem('userId')
      if (!userId) {
        throw new Error('User ID not found')
      }

      

const params = new URLSearchParams({
  page: page.toString(),
  pageSize: pageSize.toString(),
  includeItems: 'true'
});

// Status filtresi
const statusToUse = statusOverride !== undefined ? statusOverride : filters.status
if (statusToUse && statusToUse !== 'Tümü') {
  params.append('status', statusToUse)
}

// 🆕 OrderId filtresi (arama)
if (orderIdFilter && orderIdFilter.trim() !== '') {
  params.append('orderId', orderIdFilter.trim())
}

// Alıcı hesabı filtresi ('' = tümü)
const buyerToUse = buyerEmailOverride !== undefined ? buyerEmailOverride : filters.buyerEmail
if (buyerToUse) {
  params.append('buyerEmail', buyerToUse)
}

// Doğrulama filtresi ('' = tümü). Kural sunucuda (kazanç hesabıyla aynı eşik).
const verificationToUse = verificationOverride !== undefined ? verificationOverride : filters.verification
if (verificationToUse) {
  params.append('verification', verificationToUse)
}

      const response = await fetch(`${process.env.REACT_APP_API_URL}/orders/by-user/${userId}?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const data: OrdersListResponse = await response.json()
      
      setOrders(data.results)
      setPagination({
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        totalPages: Math.ceil(data.total / data.pageSize)
      })

          try {
      const statsResponse = await fetch(`${process.env.REACT_APP_API_URL}/orders/dashboard-stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      })
      
      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        setOrderStats({
          total: statsData.stats.totalOrders || 0,
          delivered: statsData.stats.deliveredOrders || 0,
          pending: statsData.stats.pendingOrders || 0,
          inTransit: statsData.stats.inTransitOrders || 0,
          cancelled: statsData.stats.cancelledOrders || 0
        })
      }
    } catch (statsError) {
      console.error('Stats fetch error:', statsError)
    }

      if (!showLoading) {
        toast.success('Siparişler güncellendi!')
      }
    } catch (error) {
      
      toast.error('Siparişler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

// 🆕 Arama değiştiğinde backend'e gönder (debounce ile)
useEffect(() => {
  if (user?.id) {
    const timeoutId = setTimeout(() => {
      const effectiveStatus = filters.status === 'Tümü' ? undefined : filters.status
      fetchOrders(1, filters.pageSize, true, effectiveStatus, filters.search.trim() || undefined)
    }, 500) // 500ms bekle

    return () => clearTimeout(timeoutId)
  }
}, [filters.search])

// ===============================================================================
// handleFilterChange FONKSİYONUNU GÜNCELLE:
// ===============================================================================

const handleFilterChange = (filterType: keyof OrderFilters, value: string | number) => {
    const newFilters = { ...filters, [filterType]: value }
    setFilters(newFilters)
    
    if (filterType === 'pageSize') {
      fetchOrders(1, Number(value), true, filters.status, filters.search)
    } else if (filterType === 'status') {
      fetchOrders(1, filters.pageSize, true, value as string, filters.search)
    } else if (filterType === 'buyerEmail') {
      fetchOrders(1, filters.pageSize, true, filters.status, filters.search, value as string)
    } else if (filterType === 'verification') {
      fetchOrders(1, filters.pageSize, true, filters.status, filters.search, filters.buyerEmail, value as string)
    }
  }

  const handlePageChange = (newPage: number) => {
    fetchOrders(newPage, filters.pageSize)
  }

  const handleRefresh = () => {
   fetchOrders(pagination.page, filters.pageSize, false)
   

  }

  const handleViewDetail = (order: ScrapeOrder) => {
    setSelectedOrder(order)
    navigate(`/orders/${encodeURIComponent(order.orderId)}`)
  }

  const handleBackToList = () => {
    setSelectedOrder(null)
    navigate('/orders')
  }

  // URL'deki orderId ile detay senkronu (deep-link / sayfa yenileme / tarayıcı geri butonu)
  useEffect(() => {
    if (!routeOrderId) { setSelectedOrder(null); return }
    if (selectedOrder?.orderId === routeOrderId) return
    // Önce yüklü listede ara
    const inList = orders.find(o => o.orderId === routeOrderId)
    if (inList) { setSelectedOrder(inList); return }
    // Listede yoksa (farklı sayfa / direkt link) tek siparişi backend'den getir
    const userId = localStorage.getItem('userId')
    if (!userId) return
    const params = new URLSearchParams({ page: '1', pageSize: '5', includeItems: 'true', orderId: routeOrderId })
    fetch(`${process.env.REACT_APP_API_URL}/orders/by-user/${userId}?${params}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}`, 'Content-Type': 'application/json' }
    })
      .then(r => (r.ok ? r.json() : null))
      .then((data: OrdersListResponse | null) => {
        const found = data?.results?.find(o => o.orderId === routeOrderId)
        if (found) setSelectedOrder(found)
        else navigate('/orders', { replace: true }) // geçersiz id → listeye dön
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeOrderId, orders])

  if (selectedOrder) {
    return <OrderDetail order={selectedOrder} onBack={handleBackToList} />
  }

// ✅ YENİ KOD (search filtresini frontend'de tutuyoruz, status backend'de):
const filteredOrders = orders

  const getStatusIcon = (status: string | null) => {
    const translated = translateStatus(status)
    
    switch (translated) {
      case 'Teslim Edildi': 
        return <CheckCircle className="w-4 h-4 text-green-600" />
      case 'Kargoda': 
        return <Clock className="w-4 h-4 text-blue-600" />
      case 'İptal Edildi': 
        return <XCircle className="w-4 h-4 text-red-600" />
      case 'Para İadesi Yapıldı': 
        return <DollarSign className="w-4 h-4 text-red-600" />
      case 'İade Talebi Mevcut': 
        return <Undo2 className="w-4 h-4 text-yellow-600" />
      case 'Test': 
        return <AlertCircle className="w-4 h-4 text-yellow-600" />
      default: 
        return <Package className="w-4 h-4 text-gray-600" />
    }
  }

  const getStatusColor = (status: string | null) => {
    const translated = translateStatus(status)
    
    switch (translated) {
      case 'Teslim Edildi': 
        return 'bg-green-100 text-green-700 border-green-200'
      case 'Kargoda': 
        return 'bg-blue-100 text-blue-700 border-blue-200'
      case 'İptal Edildi': 
        return 'bg-red-100 text-red-700 border-red-200'
      case 'Para İadesi Yapıldı': 
        return 'bg-red-100 text-red-700 border-red-200'
      case 'İade Talebi Mevcut': 
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      case 'Test': 
        return 'bg-yellow-100 text-yellow-700 border-yellow-200'
      default: 
        return 'bg-gray-100 text-gray-700 border-gray-200'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Tarih yok'
    try {
      return new Date(dateString).toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Geçersiz tarih'
    }
  }

const formatPrice = formatCurrency  // ← BASIT!

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Siparişler yükleniyor...</span>
        </div>
      </div>
    )
  }

const stats = {
  total: orderStats.total,
  delivered: orderStats.delivered,
  pending: orderStats.pending,
  inTransit: orderStats.inTransit,
  cancelled: orderStats.cancelled
}

  return (
    <>
      {/* Orders Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass border-orange-200 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Toplam Sipariş</p>
                  <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                </div>
                <Package className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="glass border-orange-200 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Bekleyen Siparişler</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="glass border-orange-200 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Teslim Edildi</p>
                  <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
  <Card className="glass border-blue-200 shadow-lg">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">Kargoda</p>
          <p className="text-2xl font-bold text-blue-600">{stats.inTransit}</p>
        </div>
        <Truck className="w-8 h-8 text-blue-600" />
      </div>
    </CardContent>
  </Card>
</motion.div>
      </div>

      {/* Orders Filters */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="glass border-orange-200 shadow-lg mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  Filtreler
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Siparişlerinizi filtreleyerek arayın
                </CardDescription>
              </div>
              <Button 
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                size="sm"
                className="border-orange-200 hover:bg-orange-50"
              >
                {refreshing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Yenile
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Sipariş Ara
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Sipariş no..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Sipariş Durumu
                </label>
                <select 
                  className="w-full h-10 px-3 py-2 bg-white/50 border border-primary/20 rounded-lg text-gray-800 backdrop-blur-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {orderStatuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Tarih Aralığı
                </label>
                <select 
                  className="w-full h-10 px-3 py-2 bg-white/50 border border-primary/20 rounded-lg text-gray-800 backdrop-blur-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                >
                  {dateRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Doğrulama
                </label>
                <select
                  className="w-full h-10 px-3 py-2 bg-white/50 border border-primary/20 rounded-lg text-gray-800 backdrop-blur-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  value={filters.verification}
                  onChange={(e) => handleFilterChange('verification', e.target.value)}
                  title="Doğrulanmadı: henüz kazanca girmemiş açık siparişler. İptal/iade edilenler bu filtrede görünmez."
                >
                  <option value="">Tümü</option>
                  <option value="verified">Doğrulandı</option>
                  <option value="unverified">Doğrulanmadı</option>
                </select>
              </div>

              {buyerAccounts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    Alıcı Hesabı
                  </label>
                  <select
                    className="w-full h-10 px-3 py-2 bg-white/50 border border-primary/20 rounded-lg text-gray-800 backdrop-blur-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all text-sm"
                    value={filters.buyerEmail}
                    onChange={(e) => handleFilterChange('buyerEmail', e.target.value)}
                  >
                    <option value="">Tüm hesaplar</option>
                    {buyerAccounts.filter(a => a.buyerEmail).map(a => (
                      <option key={a.buyerEmail!} value={a.buyerEmail!}>
                        {a.buyerEmail} ({a.total})
                      </option>
                    ))}
                    {buyerAccounts.some(a => !a.buyerEmail) && (
                      <option value="__none__">
                        Hesabı henüz okunmamış ({buyerAccounts.find(a => !a.buyerEmail)?.total ?? 0})
                      </option>
                    )}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-2">
                  Sayfa Başına
                </label>
                <select 
                  className="w-full h-10 px-3 py-2 bg-white/50 border border-primary/20 rounded-lg text-gray-800 backdrop-blur-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                  value={filters.pageSize}
                  onChange={(e) => handleFilterChange('pageSize', Number(e.target.value))}
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Orders List */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
        <Card className="glass border-orange-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-gray-800">
              Siparişler ({filteredOrders.length})
            </CardTitle>

          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              {/* Desktop Headers */}
              <div className="hidden md:grid md:grid-cols-8 gap-4 p-4 border-b border-orange-200 bg-orange-50">
                <div className="text-sm font-medium text-gray-700">Sipariş No</div>
                <div className="text-sm font-medium text-gray-700">Durum</div>
                <div className="text-sm font-medium text-gray-700">Ürün Sayısı</div>
                <div className="text-sm font-medium text-gray-700">Toplam</div>
                <div className="text-sm font-medium text-gray-700">Komisyon Tutarı</div>
                <div className="text-sm font-medium text-gray-700">Sipariş Tarihi</div>
                <div className="text-sm font-medium text-gray-700">Güncellenme</div>
                <div className="text-sm font-medium text-gray-700">İşlemler</div>
              </div>

              {/* Orders List */}
              <div className="divide-y divide-orange-200">
                {filteredOrders.length > 0 ? (
                  filteredOrders.map((order, index) => {
                    const commissionAmount = calculateOrderCommission(order)
                    const strikethrough = shouldShowCommissionStrikethrough(order.status)
                    const pending = shouldShowCommissionPending(order.status)
                    const translatedStatus = translateStatus(order.status)
                    
                    return (
                      <motion.div
                        key={order.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 * index }}
                        className="p-4 hover:bg-orange-50 transition-colors"
                      >
                        {/* Desktop Layout */}
                        <div className="hidden md:grid md:grid-cols-8 gap-4">
                          <div>
                            <OrderIdLink orderId={order.orderId} />
                          </div>
                          
                          <div>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              <span className="ml-1">{translatedStatus}</span>
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-sm font-medium text-gray-800">{order.itemCount} adet</p>
                          </div>
                          
                          <div>
                            <p className="text-sm font-medium text-gray-800">
                              {formatPrice(order.total)}
                            </p>
                          </div>
                          
<div>
  {strikethrough ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold bg-red-50 text-red-600 border border-red-200 line-through decoration-2">
      {formatPrice(commissionAmount)}
    </span>
  ) : pending ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-semibold bg-yellow-50 text-yellow-600 border border-yellow-200">
      {formatPrice(commissionAmount)}
      <Clock className="w-3 h-3" />
    </span>
  ) : (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-sm font-semibold border ${
      translatedStatus === 'Teslim Edildi' 
        ? 'bg-green-50 text-green-600 border-green-200' :
      translatedStatus === 'Kargoda' 
        ? 'bg-blue-50 text-blue-600 border-blue-200' :
      'bg-gray-50 text-gray-600 border-gray-200'
    }`}>
      {formatPrice(commissionAmount)}
    </span>
  )}
</div>
                          
                          <div>
                            <p className="text-sm text-gray-800">
                              {formatDate(order.createdAtUtc)}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-800">
                              {formatDate(order.updatedAtUtc)}
                            </p>
                          </div>
                          
                          <div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-orange-200 hover:bg-orange-100"
                              onClick={() => handleViewDetail(order)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Detay
                            </Button>
                          </div>
                        </div>

                        {/* Mobile Layout */}
                        <div className="md:hidden space-y-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Sipariş No</p>
                              <OrderIdLink orderId={order.orderId} />
                            </div>
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                              {getStatusIcon(order.status)}
                              <span className="ml-1">{translatedStatus}</span>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Ürün Sayısı:</span>
                              <p className="font-medium">{order.itemCount} adet</p>
                            </div>
                            <div>
                              <span className="text-gray-600">Toplam:</span>
                              <p className="font-medium">{formatPrice(order.total)}</p>
                            </div>
<div>
  <span className="text-gray-600">Komisyon:</span>
  {strikethrough ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md font-semibold bg-red-50 text-red-600 border border-red-200 line-through decoration-2">
      {formatPrice(commissionAmount)}
    </span>
  ) : pending ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold bg-yellow-50 text-yellow-600 border border-yellow-200">
      {formatPrice(commissionAmount)}
      <Clock className="w-3 h-3" />
    </span>
  ) : (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md font-semibold border ${
      translatedStatus === 'Teslim Edildi' 
        ? 'bg-green-50 text-green-600 border-green-200' :
      translatedStatus === 'Kargoda' 
        ? 'bg-blue-50 text-blue-600 border-blue-200' :
      'bg-gray-50 text-gray-600 border-gray-200'
    }`}>
      {formatPrice(commissionAmount)}
    </span>
  )}
</div>
                          </div>
                          
                          <div className="text-xs text-gray-600">
                            <p>Sipariş Tarihi: {formatDate(order.createdAtUtc)}</p>
                            <p>Güncellenme: {formatDate(order.updatedAtUtc)}</p>
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full border-orange-200 hover:bg-orange-100"
                            onClick={() => handleViewDetail(order)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Detayları Gör
                          </Button>
                        </div>
                      </motion.div>
                    )
                  })
                ) : (
                  <div className="p-8 text-center">
                    <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg font-medium">
                      {filters.search || filters.status !== 'Tümü' 
                        ? 'Filtrelere uygun sipariş bulunamadı' 
                        : 'Henüz sipariş bulunamadı'
                      }
                    </p>
                    
                    {(filters.search || filters.status !== 'Tümü') && (
                      <Button 
                        onClick={() => {
                          setFilters({ ...filters, search: '', status: 'Tümü' })
                        }}
                        variant="outline"
                        size="sm"
                        className="mt-3"
                      >
                        Filtreleri Temizle
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-orange-200 bg-gray-50">
                <p className="text-sm text-gray-600 text-center sm:text-left">
                  {pagination.total} sipariş içinden {filteredOrders.length} tanesi gösteriliyor
                  <br className="sm:hidden" />
                  <span className="hidden sm:inline"> • </span>
                  Sayfa {pagination.page} / {pagination.totalPages}
                </p>
                
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-orange-200 hover:bg-orange-50"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(1)}
                  >
                    İlk
                  </Button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                      const page = pagination.page <= 3 ? i + 1 : 
                                   pagination.page >= pagination.totalPages - 2 ? pagination.totalPages - 4 + i :
                                   pagination.page - 2 + i
                      
                      if (page < 1 || page > pagination.totalPages) return null
                      
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-8 h-8 text-sm rounded-md transition-colors ${
                            pagination.page === page 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-white border border-orange-200 text-gray-600 hover:bg-orange-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    })}
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-orange-200 hover:bg-orange-50"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.totalPages)}
                  >
                    Son
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </>
  )
}